import { type ComponentProps } from "react";
import { Configuration as ConfigurationView } from "./Configuration";
import { Designer as DesignerView } from "./Designer";
import { Outputs as OutputsView } from "./Outputs";
import { CalculationEditor as CalculationEditorView } from "./CalculationEditor";
import { ProjectSummary as ProjectSummaryView } from "./ProjectSummary";
import { AuthoringServicesProvider, type AuthoringServices } from "./services";
export { AuthoringServicesProvider };
export { FormConfigurationEditor, type FormConfigurationEditorProps } from "./FormConfigurationEditor";
import {
  AssetLibrary as AssetLibraryView,
  type AssetLibraryProps,
} from "./AssetLibrary";
export function AssetLibrary(props: AssetLibraryProps) {
  return (
    <div className="proof-authoring">
      <AssetLibraryView {...props} />
    </div>
  );
}
export type { AssetLibraryProps } from "./AssetLibrary";
export * from "./asset-library";
export type { AuthoringServices };
export type { EditorProps } from "./Configuration";
export { AuthoringSession } from "./session";
export { authoringStyles } from "./styles.generated";
export * from "./json-import";
export type ProjectSummaryProps = ComponentProps<typeof ProjectSummaryView>;
export function ProjectSummary(props: ProjectSummaryProps) {
  return (
    <div className="proof-authoring">
      <ProjectSummaryView {...props} />
    </div>
  );
}
const noop = () => {};
export type ConfigurationProps = ComponentProps<typeof ConfigurationView> & {
  services?: AuthoringServices;
};
export type OutputsProps = ComponentProps<typeof OutputsView> & {
  services?: AuthoringServices;
};
type RawDesignerProps = ComponentProps<typeof DesignerView>;
export type DesignerProps = Omit<
  RawDesignerProps,
  "onUndo" | "onRedo" | "canUndo" | "canRedo"
> &
  Partial<
    Pick<RawDesignerProps, "onUndo" | "onRedo" | "canUndo" | "canRedo">
  > & { services?: AuthoringServices };
export function Configuration({ services, ...props }: ConfigurationProps) {
  return (
    <AuthoringServicesProvider services={services}>
      <div className="proof-authoring">
        <ConfigurationView key={props.project.id} {...props} />
      </div>
    </AuthoringServicesProvider>
  );
}
export function Designer({
  services,
  onUndo = noop,
  onRedo = noop,
  canUndo = false,
  canRedo = false,
  ...props
}: DesignerProps) {
  return (
    <AuthoringServicesProvider services={services}>
      <div className="proof-authoring">
        <DesignerView
          key={props.project.id}
          {...props}
          onUndo={onUndo}
          onRedo={onRedo}
          canUndo={canUndo}
          canRedo={canRedo}
        />
      </div>
    </AuthoringServicesProvider>
  );
}
export function Outputs({ services, ...props }: OutputsProps) {
  return (
    <AuthoringServicesProvider services={services}>
      <div className="proof-authoring">
        <OutputsView key={props.project.id} {...props} />
      </div>
    </AuthoringServicesProvider>
  );
}

export { DatasetEditor, newDataset } from "./DatasetEditor";
import { GlobalProfiles as GlobalProfilesView } from "./GlobalProfiles";
export type GlobalProfilesProps = ComponentProps<typeof GlobalProfilesView> & {
  services?: AuthoringServices;
};
export function GlobalProfiles({ services, ...props }: GlobalProfilesProps) {
  return (
    <AuthoringServicesProvider services={services}>
      <div className="proof-authoring">
        <GlobalProfilesView
          key={`${props.project.tenantId}:${props.project.id}`}
          {...props}
        />
      </div>
    </AuthoringServicesProvider>
  );
}
import { GlobalProfileGrid as GlobalProfileGridView } from "./GlobalProfileGrid";
export type GlobalProfileGridProps = ComponentProps<
  typeof GlobalProfileGridView
> & { services?: AuthoringServices };
export function GlobalProfileGrid({
  services,
  ...props
}: GlobalProfileGridProps) {
  return (
    <AuthoringServicesProvider services={services}>
      <div className="proof-authoring">
        <GlobalProfileGridView {...props} />
      </div>
    </AuthoringServicesProvider>
  );
}
export { ProfileGrid, type ProfileGridRow } from "./ProfileGrid";
export { SheetSettings } from "./SheetSettings";
export { SheetStockEditor } from "./SheetStockEditor";
export { datasetFromSheet } from "./dataset-import";

import { CatalogManager as ResourceLibraryView } from "./ResourceLibrary";
export type ResourceLibraryProps = ComponentProps<typeof ResourceLibraryView>;
export function ResourceLibrary(props: ResourceLibraryProps) {
  return (
    <div className="proof-authoring">
      <ResourceLibraryView
        key={`${props.project.tenantId}:${props.project.id}`}
        {...props}
      />
    </div>
  );
}
export { DynamicSourceManager } from "./DynamicSources";
import {
  FontLibrary as FontLibraryView,
  type FontLibraryProps,
} from "./FontLibrary";
export function FontLibrary(props: FontLibraryProps) {
  return (
    <div className="proof-authoring">
      <FontLibraryView {...props} />
    </div>
  );
}
export type { FontLibraryProps } from "./FontLibrary";
export type {
  FontLibraryRecord,
  FontLibraryServices,
  FontLibraryArtifact,
} from "./font-library";
export {
  bindFontLibraryServices,
  snapshotFontLibraryRecords,
  createFontLibraryStyle,
  downloadFontLibraryFace,
} from "./font-library";
import { ProjectRevisionReview as ProjectRevisionReviewView } from "./ProjectRevisionReview";
export type {
  ProjectRevisionReviewProps,
  RevisionReviewServices,
  RevisionReviewReport,
} from "./ProjectRevisionReview";
export {
  downloadRevisionReview,
  bindRevisionReviewServices,
} from "./ProjectRevisionReview";
export function ProjectRevisionReview(
  props: ComponentProps<typeof ProjectRevisionReviewView>,
) {
  return (
    <div className="proof-authoring">
      <ProjectRevisionReviewView {...props} />
    </div>
  );
}
export type CalculationEditorProps = ComponentProps<
  typeof CalculationEditorView
>;
export function CalculationEditor(props: CalculationEditorProps) {
  return (
    <div className="proof-authoring">
      <CalculationEditorView {...props} />
    </div>
  );
}

import {
  ConfigurationEditor as ConfigurationEditorView,
  type ConfigurationEditorProps,
} from "./ConfigurationEditor";
export function ConfigurationEditor(props: ConfigurationEditorProps) {
  return (
    <div className="proof-authoring">
      <ConfigurationEditorView {...props} />
    </div>
  );
}
export type {
  ConfigurationEditorProps,
  ConfigurationField,
  ConfigurationValue,
} from "./ConfigurationEditor";
export { parseConfiguration } from "./configuration-fields";

import {
  ProjectExport as ProjectExportView,
  type ProjectExportProps,
} from "./ProjectExport";
export function ProjectExport(props: ProjectExportProps) {
  return (
    <div className="proof-authoring">
      <ProjectExportView {...props} />
    </div>
  );
}
export { downloadProjectPackage } from "./ProjectExport";
export type {
  ProjectExportProps,
  ProjectExportServices,
  ProjectExportSelection,
  ProjectExportArtifact,
} from "./ProjectExport";

import {
  DocumentImport as DocumentImportView,
  type DocumentImportProps,
} from "./DocumentImport";
export function DocumentImport(props: DocumentImportProps) {
  return (
    <div className="proof-authoring">
      <DocumentImportView {...props} />
    </div>
  );
}
export { downloadImportReport, snapshotImportReport } from "./DocumentImport";
import {
  ProjectArchiveImport as ProjectArchiveImportView,
  type ProjectArchiveImportProps,
} from "./ProjectArchiveImport";
export function ProjectArchiveImport(props: ProjectArchiveImportProps) {
  return (
    <div className="proof-authoring">
      <ProjectArchiveImportView {...props} />
    </div>
  );
}
export type {
  ProjectArchiveImportServices,
  ProjectArchiveImportProps,
} from "./ProjectArchiveImport";
export type {
  DocumentImportProps,
  DocumentImportSelection,
  DocumentImportServices,
} from "./DocumentImport";

import {
  BulkGeneration as BulkGenerationView,
  type BulkGenerationProps,
} from "./BulkGeneration";
export type { BulkGenerationProps } from "./BulkGeneration";
export * from "./bulk-generation";
export function BulkGeneration(props: BulkGenerationProps) {
  return (
    <div className="proof-authoring">
      <BulkGenerationView {...props} />
    </div>
  );
}

import { FieldPresetLibrary as FieldPresetLibraryView } from "./FieldPolicies";
export { FieldPolicyControls, PolicySettingsEditor } from "./FieldPolicies";
export type FieldPresetLibraryProps = ComponentProps<
  typeof FieldPresetLibraryView
> & { services?: AuthoringServices };
export function FieldPresetLibrary({
  services,
  ...props
}: FieldPresetLibraryProps) {
  return (
    <AuthoringServicesProvider services={services}>
      <div className="proof-authoring">
        <FieldPresetLibraryView {...props} />
      </div>
    </AuthoringServicesProvider>
  );
}
export { EsmProjectEditor, type EsmProjectEditorProps } from "./EsmProjectEditor";
export { VariableTypeMapper } from "./VariableTypeMapper";
